import React, { useState, useEffect, useCallback } from 'react';
import {
  Settings, Globe, Key, Gamepad2, DollarSign, Shield, AlertTriangle,
  RefreshCw, Save, Plus, Trash2,
  ToggleLeft, SlidersHorizontal
} from 'lucide-react';
import { adminService } from '../services/adminService';
import { Country, ApiKey } from '../types';
import { useToast } from '../contexts/ToastContext';
import EconomySettingsPanel from '../components/EconomySettingsPanel';
import PartnerUnlockReviews from '../components/PartnerUnlockReviews';
import AdscendReviews from '../components/AdscendReviews';
import LegacyGlobalSettingsPanel from '../components/LegacyGlobalSettingsPanel';
import PlatformSettingsPanel from '../components/PlatformSettingsPanel';
import './Config.css';

interface FeatureFlag {
  key: string;
  enabled: boolean;
  description: string | null;
  updated_at: string;
}

interface AdUnit {
  placement: string;
  enabled: boolean;
  android_unit_id: string | null;
  ios_unit_id: string | null;
  description: string | null;
  updated_at: string;
}

const FLAG_LABELS: Record<string, { label: string; desc: string }> = {
  kyc_enabled: {
    label: 'KYC Verification (Smile ID)',
    desc: 'Show KYC banner and gate withdrawals behind identity verification',
  },
};

const Config: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'countries' | 'apikeys' | 'game' | 'platform' | 'flags' | 'ads' | 'economysettings' | 'globalsettings'>('economysettings');
  const [countries, setCountries] = useState<Country[]>([]);
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [featureFlags, setFeatureFlags] = useState<FeatureFlag[]>([]);
  const [flagsLoading, setFlagsLoading] = useState(false);
  const [togglingKey, setTogglingKey] = useState<string | null>(null);
  const [adUnits, setAdUnits] = useState<AdUnit[]>([]);
  const [adUnitsLoading, setAdUnitsLoading] = useState(false);
  const [savingPlacement, setSavingPlacement] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyExpires, setNewKeyExpires] = useState('30');
  const toast = useToast();

  const loadCountries = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminService.getCountries();
      setCountries(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load countries:', error);
      setCountries([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadApiKeys = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminService.getApiKeys();
      setApiKeys(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load API keys:', error);
      setApiKeys([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadFeatureFlags = useCallback(async () => {
    try {
      setFlagsLoading(true);
      const data = await adminService.getFeatureFlags();
      setFeatureFlags(data);
    } catch (error) {
      toast.error('Error', 'Failed to load feature flags');
    } finally {
      setFlagsLoading(false);
    }
  }, [toast]);

  const handleToggleFlag = async (key: string, newValue: boolean) => {
    setTogglingKey(key);
    try {
      await adminService.updateFeatureFlag(key, newValue);
      setFeatureFlags(prev =>
        prev.map(f => f.key === key ? { ...f, enabled: newValue, updated_at: new Date().toISOString() } : f)
      );
      const label = FLAG_LABELS[key]?.label ?? key;
      toast.success('Flag Updated', `${label} is now ${newValue ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Error', 'Failed to update feature flag');
    } finally {
      setTogglingKey(null);
    }
  };

  const loadAdUnits = useCallback(async () => {
    try {
      setAdUnitsLoading(true);
      const data = await adminService.getAdUnits();
      setAdUnits(data);
    } catch (error) {
      toast.error('Error', 'Failed to load ad units');
    } finally {
      setAdUnitsLoading(false);
    }
  }, [toast]);

  const handleAdUnitField = (placement: string, field: keyof AdUnit, value: string | boolean) => {
    setAdUnits(prev =>
      prev.map(u => (u.placement === placement ? { ...u, [field]: value } : u))
    );
  };

  const handleSaveAdUnit = async (placement: string) => {
    const unit = adUnits.find(u => u.placement === placement);
    if (!unit) return;
    setSavingPlacement(placement);
    try {
      await adminService.updateAdUnit(placement, {
        enabled: unit.enabled,
        android_unit_id: unit.android_unit_id ?? '',
        ios_unit_id: unit.ios_unit_id ?? '',
      });
      setAdUnits(prev =>
        prev.map(u => (u.placement === placement ? { ...u, updated_at: new Date().toISOString() } : u))
      );
      toast.success('Ad Unit Saved', `${placement} updated. Takes effect on the next app launch.`);
    } catch (error) {
      toast.error('Error', 'Failed to save ad unit');
    } finally {
      setSavingPlacement(null);
    }
  };

  useEffect(() => {
    if (activeTab === 'countries') {
      loadCountries();
    } else if (activeTab === 'apikeys') {
      loadApiKeys();
    } else if (activeTab === 'flags') {
      loadFeatureFlags();
    } else if (activeTab === 'ads') {
      loadAdUnits();
    }
  }, [activeTab, loadCountries, loadApiKeys, loadFeatureFlags, loadAdUnits]);

  const handleUpdateCountry = async (countryId: number, field: string, value: any) => {
    try {
      await adminService.updateCountry(countryId, { [field]: value });
      toast.success('Country Updated', 'Settings saved successfully');
      await loadCountries();
    } catch (error) {
      toast.error('Error', 'Failed to update country');
    }
  };

  const handleCreateApiKey = async () => {
    if (!newKeyName.trim()) {
      toast.warning('Validation', 'Please enter a key name');
      return;
    }

    try {
      await adminService.createApiKey(newKeyName, parseInt(newKeyExpires));
      toast.success('API Key Created', 'New key has been generated');
      setNewKeyName('');
      setNewKeyExpires('30');
      await loadApiKeys();
    } catch (error) {
      toast.error('Error', 'Failed to create API key');
    }
  };

  const handleRotateApiKey = async (keyId: number, keyName: string) => {
    if (!window.confirm(`Rotate API key "${keyName}"? The old key will be invalidated.`)) return;

    try {
      await adminService.rotateApiKey(keyId);
      toast.success('API Key Rotated', 'New key generated, old key revoked');
      await loadApiKeys();
    } catch (error) {
      toast.error('Error', 'Failed to rotate API key');
    }
  };

  const handleDeleteApiKey = async (keyId: number) => {
    if (!window.confirm('Are you sure you want to delete this API key?')) return;

    try {
      await adminService.deleteApiKey(keyId);
      toast.success('API Key Deleted', 'Key has been revoked');
      await loadApiKeys();
    } catch (error) {
      toast.error('Error', 'Failed to delete API key');
    }
  };

  return (
    <div className="config-page-modern">
      {/* Header */}
      <div className="config-header">
        <div className="header-content">
          <h1><Settings size={28} /> Configuration</h1>
          <p className="header-subtitle">Manage platform settings and configurations</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="config-tabs">
        <button
          className={activeTab === 'countries' ? 'active' : ''}
          onClick={() => setActiveTab('countries')}
        >
          <Globe size={18} /> Countries
        </button>
        <button
          className={activeTab === 'apikeys' ? 'active' : ''}
          onClick={() => setActiveTab('apikeys')}
        >
          <Key size={18} /> API Keys
        </button>
        <button
          className={activeTab === 'game' ? 'active' : ''}
          onClick={() => setActiveTab('game')}
        >
          <Gamepad2 size={18} /> Protocol Settings
        </button>
        <button
          className={activeTab === 'platform' ? 'active' : ''}
          onClick={() => setActiveTab('platform')}
        >
          <Shield size={18} /> Platform
        </button>
        <button
          className={activeTab === 'flags' ? 'active' : ''}
          onClick={() => setActiveTab('flags')}
        >
          <ToggleLeft size={18} /> Feature Flags
        </button>
        <button
          className={activeTab === 'ads' ? 'active' : ''}
          onClick={() => setActiveTab('ads')}
        >
          <DollarSign size={18} /> Ad Units
        </button>
        <button
          className={activeTab === 'economysettings' ? 'active' : ''}
          onClick={() => setActiveTab('economysettings')}
        >
          <SlidersHorizontal size={18} /> Economy Settings
        </button>
        <button
          className={activeTab === 'globalsettings' ? 'active' : ''}
          onClick={() => setActiveTab('globalsettings')}
        >
          <Settings size={18} /> Global Settings
        </button>
      </div>

      {/* Countries Tab */}
      {activeTab === 'countries' && (
        <div className="config-content">
          <div className="section-header">
            <div>
              <h3>Country Configuration</h3>
              <p style={{ color: '#6b7280', margin: '6px 0 0', fontSize: '14px' }}>
                Country activation is managed here. Withdrawal fees are managed in Platform Settings.
              </p>
            </div>
            <button onClick={loadCountries} className="refresh-btn" disabled={loading}>
              <RefreshCw className={loading ? 'spin' : ''} size={18} />
              Refresh
            </button>
          </div>
          
          {loading ? (
            <div className="loading-state">
              <RefreshCw className="spin" size={32} />
              <span>Loading countries...</span>
            </div>
          ) : (
            <div className="table-container">
              <table className="config-table">
                <thead>
                  <tr>
                    <th>Country</th>
                    <th>Currency</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {countries.length > 0 ? countries.map((country) => (
                    <tr key={country.id}>
                      <td>
                        <div className="country-cell">
                          <span className="country-name">{country.name}</span>
                          <span className="country-code">{country.code}</span>
                        </div>
                      </td>
                      <td>
                        <span className="currency-badge">
                          {country.currency_symbol} {country.currency_code}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${country.is_active ? 'active' : 'inactive'}`}>
                          {country.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <button
                          onClick={() => handleUpdateCountry(country.id, 'is_active', !country.is_active)}
                          className={`toggle-btn ${country.is_active ? 'deactivate' : 'activate'}`}
                        >
                          {country.is_active ? 'Disable' : 'Enable'}
                        </button>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={4} className="empty-state">No countries found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* API Keys Tab */}
      {activeTab === 'apikeys' && (
        <div className="config-content">
          <div className="create-section">
            <h3><Plus size={18} /> Create New API Key</h3>
            <div className="create-form">
              <input
                type="text"
                placeholder="Key name (e.g., Mobile App, Integration)"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
              />
              <div className="input-with-suffix">
                <input
                  type="number"
                  placeholder="Days"
                  value={newKeyExpires}
                  onChange={(e) => setNewKeyExpires(e.target.value)}
                  min="1"
                  max="365"
                />
                <span className="suffix">days</span>
              </div>
              <button onClick={handleCreateApiKey} className="create-btn">
                <Plus size={18} /> Generate Key
              </button>
            </div>
          </div>

          {loading ? (
            <div className="loading-state">
              <RefreshCw className="spin" size={32} />
              <span>Loading API keys...</span>
            </div>
          ) : (
            <div className="table-container">
              <table className="config-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Prefix</th>
                    <th>Status</th>
                    <th>Expires</th>
                    <th>Last Used</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {apiKeys.length > 0 ? apiKeys.map((key) => (
                    <tr key={key.id}>
                      <td><strong>{key.key_name}</strong></td>
                      <td><code className="key-prefix">{key.key_prefix}...</code></td>
                      <td>
                        <span className={`status-badge ${key.is_active ? 'active' : 'inactive'}`}>
                          {key.is_active ? 'Active' : 'Revoked'}
                        </span>
                      </td>
                      <td>{key.expires_at ? new Date(key.expires_at).toLocaleDateString() : 'Never'}</td>
                      <td>{key.last_used_at ? new Date(key.last_used_at).toLocaleDateString() : 'Never'}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          {key.is_active && (
                            <button
                              onClick={() => handleRotateApiKey(key.id, key.key_name)}
                              className="rotate-btn"
                              title="Rotate key"
                              style={{ 
                                background: 'none', border: '1px solid #410C6D', borderRadius: '6px',
                                color: '#410C6D', padding: '4px 8px', cursor: 'pointer', display: 'flex',
                                alignItems: 'center', gap: '4px', fontSize: '13px'
                              }}
                            >
                              <RefreshCw size={14} /> Rotate
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteApiKey(key.id)}
                            className="delete-btn"
                            title="Revoke key"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={6} className="empty-state">No API keys found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Protocol Settings Tab */}
      {activeTab === 'game' && (
        <div className="config-content">
          <div className="config-unavailable-panel" role="status">
            <AlertTriangle size={20} />
            <div>
              <h3>Protocol settings unavailable</h3>
              <p>
                These controls are not connected to an authenticated backend setting yet.
                They are read-only here so no local-only change can appear to be saved.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Platform Settings Tab */}
      {activeTab === 'platform' && <PlatformSettingsPanel />}

      {/* Feature Flags Tab */}
      {activeTab === 'flags' && (
        <div className="config-content">
          <div className="section-header">
            <h3>Feature Flags</h3>
            <button onClick={loadFeatureFlags} className="refresh-btn" disabled={flagsLoading}>
              <RefreshCw className={flagsLoading ? 'spin' : ''} size={18} />
              Refresh
            </button>
          </div>
          <p style={{ color: '#666', marginBottom: '24px', fontSize: '14px' }}>
            Toggle features on or off without a deployment. Changes take effect on the next mobile app launch.
          </p>

          {flagsLoading ? (
            <div className="loading-state">
              <RefreshCw className="spin" size={32} />
              <span>Loading flags...</span>
            </div>
          ) : (
            <div className="settings-card full-width">
              <div className="toggle-settings">
                {featureFlags.length === 0 && (
                  <p style={{ color: '#999', padding: '16px 0' }}>No feature flags found.</p>
                )}
                {featureFlags.map((flag) => {
                  const meta = FLAG_LABELS[flag.key];
                  const isToggling = togglingKey === flag.key;
                  return (
                    <div className="toggle-item" key={flag.key}>
                      <div className="toggle-info">
                        <span className="toggle-label">{meta?.label ?? flag.key}</span>
                        <span className="toggle-desc">
                          {meta?.desc ?? flag.description ?? ''}
                          {' '}
                          <span style={{ color: '#aaa', fontSize: '12px' }}>
                            Last updated: {new Date(flag.updated_at).toLocaleString()}
                          </span>
                        </span>
                      </div>
                      <label className="switch" style={{ opacity: isToggling ? 0.5 : 1 }}>
                        <input
                          type="checkbox"
                          checked={flag.enabled}
                          disabled={isToggling}
                          onChange={(e) => handleToggleFlag(flag.key, e.target.checked)}
                        />
                        <span className="slider"></span>
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Ad Units Tab */}
      {activeTab === 'ads' && (
        <div className="config-content">
          <div className="section-header">
            <h3>Ad Units</h3>
            <button onClick={loadAdUnits} className="refresh-btn" disabled={adUnitsLoading}>
              <RefreshCw className={adUnitsLoading ? 'spin' : ''} size={18} />
              Refresh
            </button>
          </div>
          <p style={{ color: '#666', marginBottom: '24px', fontSize: '14px' }}>
            Change LevelPlay ad unit IDs and toggle placements without a release.
            Use the ad unit IDs from the LevelPlay dashboard, not AdMob IDs: AdMob
            and Meta bid inside LevelPlay. Leave an ID blank to use the one built
            into the app. Changes take effect on the next mobile app launch.
          </p>

          {adUnitsLoading ? (
            <div className="loading-state">
              <RefreshCw className="spin" size={32} />
              <span>Loading ad units...</span>
            </div>
          ) : (
            <>
              {adUnits.length === 0 && (
                <p style={{ color: '#999', padding: '16px 0' }}>No ad units found.</p>
              )}
              {adUnits.map((unit) => {
                const isSaving = savingPlacement === unit.placement;
                return (
                  <div className="settings-card full-width" key={unit.placement} style={{ marginBottom: '16px' }}>
                    <div className="toggle-item" style={{ borderBottom: '1px solid #eee', paddingBottom: '12px' }}>
                      <div className="toggle-info">
                        <span className="toggle-label" style={{ textTransform: 'capitalize' }}>{unit.placement}</span>
                        <span className="toggle-desc">
                          {unit.description ?? ''}
                          {' '}
                          <span style={{ color: '#aaa', fontSize: '12px' }}>
                            Last updated: {new Date(unit.updated_at).toLocaleString()}
                          </span>
                        </span>
                      </div>
                      <label className="switch" style={{ opacity: isSaving ? 0.5 : 1 }}>
                        <input
                          type="checkbox"
                          checked={unit.enabled}
                          disabled={isSaving}
                          onChange={(e) => handleAdUnitField(unit.placement, 'enabled', e.target.checked)}
                        />
                        <span className="slider"></span>
                      </label>
                    </div>
                    <div className="settings-grid" style={{ marginTop: '12px' }}>
                      <div className="setting-item">
                        <label>Android Unit ID</label>
                        <input
                          type="text"
                          placeholder="LevelPlay ad unit ID (blank = built-in)"
                          value={unit.android_unit_id ?? ''}
                          onChange={(e) => handleAdUnitField(unit.placement, 'android_unit_id', e.target.value)}
                        />
                      </div>
                      <div className="setting-item">
                        <label>iOS Unit ID</label>
                        <input
                          type="text"
                          placeholder="LevelPlay ad unit ID (blank = built-in)"
                          value={unit.ios_unit_id ?? ''}
                          onChange={(e) => handleAdUnitField(unit.placement, 'ios_unit_id', e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="save-section">
                      <button
                        onClick={() => handleSaveAdUnit(unit.placement)}
                        className="save-btn"
                        disabled={isSaving}
                      >
                        {isSaving ? <RefreshCw className="spin" size={18} /> : <Save size={18} />}
                        {isSaving ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
      {activeTab === 'economysettings' && <><EconomySettingsPanel /><PartnerUnlockReviews /><AdscendReviews /></>}
      {activeTab === 'globalsettings' && <LegacyGlobalSettingsPanel />}

    </div>
  );
};

export default Config;
